import { zodResolver } from "@hookform/resolvers/zod";
import CancelIcon from "@mui/icons-material/Cancel";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import React, { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import {
  BulkProposalTarget,
  buildBulkCompletionText,
  buildBulkErrorAlertText,
  registerBulkProposals,
} from "../../../../api/bulkInvestmentProposals";
import {
  PropallyAgentV1Api,
  Id,
  ResponseMessage,
  InvestmentProposal,
  InvestmentProposalAttachmentFile,
  InvestmentProposalRequest,
  AgentMessageTemplate,
} from "../../../../api/generated/src";
import {
  IGNORED_INVESTMENT_OFFER_MESSAGE,
  getCompanyAlreadyMatchedMessage,
  isCompanyAlreadyMatchedResponse,
  isIgnoredOfferResponse,
  logUnexpectedOfferError,
} from "../../../../api/offerResponse";
import {
  fetchScheduledInvestmentConflicts,
  fetchScheduledInvestmentConflictsBySearch,
} from "../../../../api/scheduledProposals";
import { BULK_MAX_TARGET_USER_COUNT } from "../../../../constants/BulkProposal";
import { UserSearchEnvelopeRequestType } from "../../../../types/UsersSearch";
import { MAX_LENGTH_ERROR, REQUIRED_ERROR } from "../../../../utils/message";
import { datetimeLocalMin, scheduledAtSchema, toProposalDateRequest } from "../../../../utils/proposalSchedule";
import { buildInvestmentReplaceConfirmText, resolveScheduledConflict } from "../../../../utils/scheduledConflict";
import Button from "../../../Atoms/Button";
import TextField from "../../../Atoms/TextField";
import { ConfirmDialog } from "../../../Molecules/ConfirmDialog";
import Modal from "../../../Molecules/Modal";
import { DoingOverlay } from "../../../Organisms/DoingOverlay";

// 日次上限で受け付けられなかった場合（ignored）はレコードが作成されず id が無い
type RegisterProposalResult = { id?: number; ignored: boolean };

const registerProposal = async (
  userId: number,
  proposal_content: string,
  short_appeal_message?: string,
  proposal_date?: string,
  agent_message_template_id?: number,
  replace_same_day?: boolean
): Promise<RegisterProposalResult> => {
  const request = {
    user_id: userId,
    proposal_content: proposal_content,
    short_appeal_message: short_appeal_message,
    proposal_date: proposal_date,
    agent_message_template_id: agent_message_template_id,
    replace_same_day: replace_same_day,
  } as InvestmentProposalRequest;
  return new Promise((resolve, reject) => {
    new PropallyAgentV1Api().agentV1InvestmentProposalsPost(
      {
        investmentProposalRequest: request,
      },
      (error: object, data: Id, response: object) => {
        if (error) {
          logUnexpectedOfferError(error, response);
          // 判定ヘルパー（isIgnoredOfferResponse 等）と揃え、生の response.body から判定できるようにする。
          // ネットワークエラー等で response が無いときは元の error を渡し、ログから原因が消えないようにする
          reject(response ?? error);
        } else {
          resolve({ id: data?.id, ignored: isIgnoredOfferResponse(response) });
        }
      }
    );
  });
};

const updateProposal = async (id: number, proposal_content: string): Promise<ResponseMessage> => {
  const request = {
    proposal_content: proposal_content,
  } as InvestmentProposalRequest;
  return new Promise((resolve, reject) => {
    new PropallyAgentV1Api().agentV1InvestmentProposalsInvestmentProposalIdPatch(
      id,
      {
        investmentProposalRequest: request,
      },
      (error: object, data: ResponseMessage, response: object) => {
        if (error) {
          console.error(error);
          reject(error);
        } else {
          resolve(data);
        }
      }
    );
  });
};

const postProposalFile = async (id: number, file: File): Promise<ResponseMessage> => {
  return new Promise((resolve, reject) => {
    new PropallyAgentV1Api().agentV1InvestmentProposalsInvestmentProposalIdFilesPost(
      id,
      { imageBinary: file },
      (error: object, data: ResponseMessage, response: object) => {
        if (error) {
          reject(response);
        } else {
          resolve(data);
        }
      }
    );
  });
};

const registerProposalSchema = z.object({
  shortAppealMessage: z.string().min(1, REQUIRED_ERROR).max(70, MAX_LENGTH_ERROR(70)),
  proposal_content: z.string().min(1, REQUIRED_ERROR).max(1000, MAX_LENGTH_ERROR(1000)),
  scheduledAt: scheduledAtSchema,
  files: z.array(z.custom<File>()),
});
type RegisterProposalSchema = z.infer<typeof registerProposalSchema>;

// Propsには登録時と編集時でそれぞれ以下を渡す
// 呼び出し元 | userId     | proposal
// ----------|------------|---------
// 登録時     |対象ユーザID配列 | undefined
// 編集時     |undefined   | 対象提案情報
// 絞り込み条件で送る場合に渡す。userIds の代わりに条件そのものをサーバーへ送り、
// 対象の確定は一覧と同じ絞り込みでサーバー側が行う。
// totalCount は送信前に画面へ出す「対象は何名か」の表示用（想定より多いまま送るのを防ぐ）。
export interface BulkConditionTarget {
  search: UserSearchEnvelopeRequestType;
  totalCount: number;
}

interface Props {
  userIds?: number[];
  proposal?: InvestmentProposal;
  selectedTemplateId?: string;
  templates?: AgentMessageTemplate[];
  isBulkReProposal?: boolean;
  latestProposalDateForSingle?: string;
  // latestProposalDateForSingle が送信済み(sent)か送信予定(scheduled)かを表す。
  // 未指定なら送信済みとして扱う（API が status を返さない経路でも従来の挙動を保つ）
  latestProposalStatusForSingle?: string;
  conditionTarget?: BulkConditionTarget;
}

const InvestmentProposalForm: React.FC<Props> = (props) => {
  const navigate = useNavigate();

  // 条件指定があればそちらを優先。チェック選択は2名以上のみ一括API（1名は従来の単発API）。
  const bulkTarget: BulkProposalTarget | null = React.useMemo(() => {
    if (props.conditionTarget) return { type: "search", search: props.conditionTarget.search };
    if (props.userIds && props.userIds.length > 1) return { type: "user_ids", userIds: props.userIds };
    return null;
  }, [props.conditionTarget, props.userIds]);

  const bulkTargetCount = props.conditionTarget ? props.conditionTarget.totalCount : props.userIds?.length ?? 0;

  // サーバー側の上限（MAX_TARGET_USER_COUNT）を超える対象はどのみち 400 で弾かれるため、
  // 件数が分かっている時点で送信ボタンごと塞ぎ、条件を絞り直す導線へ誘導する。
  const overBulkCap = bulkTargetCount > BULK_MAX_TARGET_USER_COUNT;

  const [hasProposed] = useState<boolean>(!!props.proposal || !!props.isBulkReProposal);
  const [sendingStatus, setSendingStatus] = useState<"notSending" | "sending" | "hasSent">("notSending");
  const [files, setFiles] = useState<File[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [blockedModalOpen, setBlockedModalOpen] = useState(false);
  const [blockedAcknowledged, setBlockedAcknowledged] = useState(false);
  const [completionText, setCompletionText] = useState<string>("送信完了しました");
  const sendLockRef = React.useRef(false);
  // 同日置き換えの確認モーダル。確認中の送信内容を保持し、「置き換える」で replace_same_day 付きで送る
  const [replaceModalOpen, setReplaceModalOpen] = useState(false);
  const [replaceModalText, setReplaceModalText] = useState("");
  const pendingSendRef = React.useRef<{
    userIds: number[];
    proposalContent: string;
    shortAppealMessage: string | undefined;
    proposalDate: string | undefined;
    files: File[];
  } | null>(null);
  // 予約重複チェック中の二重送信を防ぐ（送信本体の sendLockRef はチェック後に立つため別に持つ）
  const conflictCheckRef = React.useRef(false);

  const latestProposalDateForSingle = React.useMemo(() => {
    if (props.latestProposalDateForSingle) return props.latestProposalDateForSingle;
    if (props.proposal?.proposal_date) return props.proposal.proposal_date;
    return undefined;
  }, [props.latestProposalDateForSingle, props.proposal?.proposal_date]);

  // 「本日送信済みブロック」の判定に使う最新提案の状態。日時と同じ優先順で解決する
  const latestProposalStatusForSingle = React.useMemo(() => {
    if (props.latestProposalDateForSingle) return props.latestProposalStatusForSingle;
    if (props.proposal?.proposal_date) return props.proposal.status;
    return undefined;
  }, [
    props.latestProposalDateForSingle,
    props.latestProposalStatusForSingle,
    props.proposal?.proposal_date,
    props.proposal?.status,
  ]);

  const hasProposedToday = React.useMemo(() => {
    if (props.isBulkReProposal) return false;
    if (!latestProposalDateForSingle) return false;
    // 予約中（送信予定）は「本日送信済み」ではない。同じ日への予約は conflicts チェック →
    // 置き換え確認モーダルに任せる（ここでブロックすると置き換えの導線ごと塞いでしまう）。
    // status が無い場合は送信済み扱いのまま＝従来どおりブロックする
    if (latestProposalStatusForSingle === "scheduled") return false;
    return dayjs(latestProposalDateForSingle).isSame(dayjs(), "day");
  }, [props.isBulkReProposal, latestProposalDateForSingle, latestProposalStatusForSingle]);

  React.useEffect(() => {
    if (!props.isBulkReProposal && hasProposedToday && !blockedAcknowledged) {
      setBlockedModalOpen(true);
    }
  }, [props.isBulkReProposal, hasProposedToday, blockedAcknowledged]);

  React.useEffect(() => {
    if (!hasProposedToday) {
      setBlockedModalOpen(false);
      setBlockedAcknowledged(false);
    }
  }, [hasProposedToday]);

  const methods = useForm<RegisterProposalSchema>({
    resolver: zodResolver(registerProposalSchema),
    mode: "onBlur",
    criteriaMode: "all",
    shouldFocusError: false,
    defaultValues: {
      shortAppealMessage: props.proposal?.short_appeal_message ? props.proposal.short_appeal_message : "",
      proposal_content: props.proposal?.proposal_content || "",
      scheduledAt: "",
      files: [],
    },
  });

  // テンプレートが選択されたら内容を自動入力
  React.useEffect(() => {
    if (props.selectedTemplateId && props.templates) {
      const selectedTemplate = props.templates.find((t) => String(t.id) === props.selectedTemplateId);
      if (selectedTemplate) {
        methods.setValue("proposal_content", selectedTemplate.content, { shouldValidate: true });
        methods.setValue("shortAppealMessage", selectedTemplate.short_appeal_message || "", { shouldValidate: true });
        // Clear any existing errors after setting values
        methods.clearErrors(["proposal_content", "shortAppealMessage"]);
      }
    }
  }, [props.selectedTemplateId, props.templates, methods]);

  const sentFileUrls: string[] = props.proposal?.investment_proposal_attachment_files.map(
    (v: InvestmentProposalAttachmentFile) => v.file_url
  );

  // テンプレート別分析のため、本文の元にしたテンプレートを送信時に記録する。
  // 本文を編集して送った場合も選択したテンプレートの実績として扱う。
  const selectedTemplateIdForRequest = React.useMemo(() => {
    if (!props.selectedTemplateId) return undefined;
    const templateId = Number(props.selectedTemplateId);
    return Number.isNaN(templateId) ? undefined : templateId;
  }, [props.selectedTemplateId]);

  // 提案登録処理
  const registerProposalsForUsers = async (
    userIds: number[],
    proposalContent: string,
    shortAppealMessage: string | undefined,
    proposalDate: string | undefined,
    files: File[],
    replaceSameDay = false
  ) => {
    if (sendLockRef.current) return;
    sendLockRef.current = true;
    setSendingStatus("sending");

    let sentSuccessfully = false;
    try {
      // 複数人宛て（チェック選択2名以上・条件指定）は一括登録APIへ。1件ずつ並列に投げると
      // 件数に比例して ALB / クライアントの60秒タイムアウトに到達し、大量に失敗していた。
      if (bulkTarget) {
        // 添付は UI が無効化されており到達しないため、一括送信では扱わない。
        const result = await registerBulkProposals({
          target: bulkTarget,
          proposalContent,
          shortAppealMessage,
          proposalDate,
          agentMessageTemplateId: selectedTemplateIdForRequest,
          isReProposal: !!props.isBulkReProposal,
          replaceSameDay,
        });

        // 1件も受け付けられなかった場合は送信完了の動線に乗せない。
        // accepted 0 の主因は「一覧が古く、実は本日提案済み／提案済みに移っていた」ことなので、
        // この画面で同じ対象を送り直しても結果は変わらない。理由を alert で伝えたうえで
        // navigate(-1) で一覧へ戻る（一覧ページが再マウントされ、再取得と選択解除が行われる）。
        // このとき入力した件名・本文は破棄されるが、対象を選び直す以上その文面は別の相手宛てになるうえ、
        // テンプレート機能があり再入力コストも限定的なため、割り切って受容する。
        if ((result.accepted_count ?? 0) === 0) {
          alert(buildBulkCompletionText(result));
          setSendingStatus("notSending");
          navigate(-1);
          return;
        }

        setCompletionText(buildBulkCompletionText(result));
        sentSuccessfully = true;
        setSendingStatus("hasSent");
        return;
      }

      const results = await Promise.allSettled(
        userIds.map(async (userId) => {
          const response = await registerProposal(
            userId,
            proposalContent,
            shortAppealMessage,
            proposalDate,
            selectedTemplateIdForRequest,
            replaceSameDay
          );
          // 日次上限で受け付けられなかった場合はレコードが無いため添付も送らない
          const proposalId = response.id;
          if (!response.ignored && proposalId != null && files.length > 0) {
            await Promise.all(files.map((file) => postProposalFile(proposalId, file)));
          }
          return response;
        })
      );

      const failures = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      if (failures.length > 0) {
        // 会社単位でマッチ済み（本人または同じ会社の別プランナー）の場合は理由が分かる文言を優先して出す。
        // 複数件が来ても他の失敗理由を隠さないよう every で判定する
        // （現状は上の bulkTarget ガードにより最大1件しか到達しない）
        const isCompanyAlreadyMatched = failures.every((f) => isCompanyAlreadyMatchedResponse(f.reason));
        alert(
          isCompanyAlreadyMatched
            ? getCompanyAlreadyMatchedMessage(failures[0].reason)
            : `送信に失敗しました（${failures.length}/${userIds.length}件）。\n時間をおいて再度お試しください。`
        );
        setSendingStatus("notSending");
        return;
      }

      // 日次オファー上限で受け付けられなかった場合（200 ignored）は実際には未送信のため、
      // 成功動線に乗せない。一括の accepted 0 と同じく理由を alert で伝えて一覧へ戻る
      // （この経路は単発のみだが、念のため全件 ignored のときに限って弾く）
      const ignoredCount = results.filter((r) => r.status === "fulfilled" && r.value.ignored).length;
      if (ignoredCount === userIds.length) {
        alert(IGNORED_INVESTMENT_OFFER_MESSAGE);
        setSendingStatus("notSending");
        navigate(-1);
        return;
      }

      sentSuccessfully = true;
      setSendingStatus("hasSent");
    } catch (error) {
      console.error(error);
      // 一括送信は上限超過（400）やチャンク途中失敗（500）で対処法が変わるため、理由に応じた文言を出す
      alert(bulkTarget ? buildBulkErrorAlertText(error) : "エラーが発生しました。\n時間をおいて再度お試しください。");
      setSendingStatus("notSending");
    } finally {
      if (!sentSuccessfully) {
        sendLockRef.current = false;
      }
    }
  };

  // 送信予約日時が入っている場合だけ、同じ日にすでに予約があるユーザーがいないか事前に確認する。
  // いれば置き換え確認モーダルを挟み、いなければ従来どおりそのまま送信する。
  //
  // 絞り込み条件での一括提案（props.conditionTarget）は対象ユーザーが送信時にサーバー側で確定するため、
  // ID の代わりに**送信に使うのと同じ search エンベロープと re_proposal** を渡して数えてもらう。
  // どの経路でも decision の扱い（過去日時スキップ・fail-open の警告・置き換えモーダル）は共通。
  const requestSendWithConflictCheck = async (
    userIds: number[],
    proposalContent: string,
    shortAppealMessage: string | undefined,
    proposalDate: string | undefined,
    files: File[]
  ) => {
    if (conflictCheckRef.current) return;
    conflictCheckRef.current = true;

    // 送信時（registerBulkProposals）と同じ target / re_proposal を使う。ここがズレると
    // 確認した件数と実際に置き換わる件数が食い違う
    const searchTarget = bulkTarget?.type === "search" ? bulkTarget.search : null;
    const isReProposalDelivery = !!props.isBulkReProposal;

    try {
      const decision = await resolveScheduledConflict({
        proposalDate,
        targetIds: userIds,
        resolvesTargetOnServer: Boolean(searchTarget),
        fetchConflictCount: async (ids, date) => {
          const conflict = searchTarget
            ? await fetchScheduledInvestmentConflictsBySearch(searchTarget, date, isReProposalDelivery)
            : await fetchScheduledInvestmentConflicts(ids, date);
          return conflict.conflictCount;
        },
      });

      if (decision.kind === "confirm") {
        pendingSendRef.current = { userIds, proposalContent, shortAppealMessage, proposalDate, files };
        setReplaceModalText(buildInvestmentReplaceConfirmText(decision.conflictCount));
        setReplaceModalOpen(true);
        return;
      }

      await registerProposalsForUsers(userIds, proposalContent, shortAppealMessage, proposalDate, files);
    } finally {
      conflictCheckRef.current = false;
    }
  };

  const handleClickReplaceButton = () => {
    const pending = pendingSendRef.current;
    setReplaceModalOpen(false);
    pendingSendRef.current = null;
    if (!pending) return;

    void registerProposalsForUsers(
      pending.userIds,
      pending.proposalContent,
      pending.shortAppealMessage,
      pending.proposalDate,
      pending.files,
      true
    );
  };

  // キャンセル時は送信しない。フォームの入力値には触れないので、そのまま日時を直して送り直せる
  const handleCancelReplace = () => {
    setReplaceModalOpen(false);
    pendingSendRef.current = null;
  };

  const onSubmit: SubmitHandler<RegisterProposalSchema> = (data) => {
    if (sendLockRef.current || sendingStatus !== "notSending") return;

    if (props.isBulkReProposal && bulkTargetCount === 0) {
      alert("提案対象のユーザーが選択されていません。");
      return;
    }

    if (overBulkCap) {
      alert(
        `対象が上限（${BULK_MAX_TARGET_USER_COUNT.toLocaleString()}名）を超えているため送信できません。\n一覧で条件を追加して対象を絞ってください。`
      );
      return;
    }

    if (!props.isBulkReProposal && hasProposedToday) {
      setBlockedAcknowledged(false);
      setBlockedModalOpen(true);
      return;
    }

    if (hasProposed) {
      // 提案済み（個別再提案・一括再提案）の場合は確認モーダルを表示
      setModalOpen(true);
      return;
    }

    if (props.conditionTarget || props.userIds) {
      const finalShortAppealMessage = data.shortAppealMessage || undefined;
      const proposalDate = toProposalDateRequest(data.scheduledAt);
      void requestSendWithConflictCheck(
        props.userIds ?? [],
        data.proposal_content,
        finalShortAppealMessage,
        proposalDate,
        data.files
      );
    }
  };

  const handleClickProcessButton = async () => {
    if (sendLockRef.current || sendingStatus !== "notSending") return;

    // 確認モーダルを開いている間に予約日時が過去になっているケースがあるため、送信直前に検証し直す
    // （過去日時は即時送信扱いになり、置き換え確認も出ないまま発火してしまう）
    if (!(await methods.trigger("scheduledAt"))) {
      setModalOpen(false);
      return;
    }

    const data = methods.getValues();
    const finalShortAppealMessage = data.shortAppealMessage || undefined;
    const proposalDate = toProposalDateRequest(data.scheduledAt);

    if (!props.isBulkReProposal && hasProposedToday) {
      setBlockedAcknowledged(false);
      setBlockedModalOpen(true);
      setModalOpen(false);
      setSendingStatus("notSending");
      return;
    }

    if (bulkTargetCount > 0) {
      setModalOpen(false);
      // 一括再提案の場合（既存の提案をdiscardして再登録する）
      void requestSendWithConflictCheck(
        props.userIds ?? [],
        data.proposal_content,
        finalShortAppealMessage,
        proposalDate,
        data.files
      );
    } else if (props.isBulkReProposal) {
      alert("提案対象のユーザーが選択されていません。");
      setModalOpen(false);
    } else if (props.proposal) {
      setModalOpen(false);
      // 個別再提案の場合（既存の提案をdiscardして再登録する）
      void requestSendWithConflictCheck(
        [props.proposal.user_id],
        data.proposal_content,
        finalShortAppealMessage,
        proposalDate,
        data.files
      );
    }
  };

  const handleOnAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    // TODO: 同一ファイル名がある場合は追加しないようにする
    if (!e.target.files) return;
    // NOTE: setState は更新が非同期で、タイミングの保証が難しいため setValue を先に実行する
    methods.setValue("files", [...files, ...e.target.files]);
    setFiles([...files, ...e.target.files]);
  };

  const handleOnRemoveImage = (index: number) => {
    const newImages = [...files];
    newImages.splice(index, 1);

    // NOTE: setState は更新が非同期で、タイミングの保証が難しいため setValue を先に実行する
    methods.setValue("files", newImages);
    setFiles(newImages);
  };

  if (sendingStatus === "sending") return <DoingOverlay open={true} text="送信中..." />;

  const previewList = files.map((file, i) => (
    <Box
      key={file.name}
      sx={{
        position: "relative",
        width: "80px",
        m: 2,
      }}
    >
      <IconButton
        aria-label="delete image"
        sx={{
          position: "absolute",
          top: -15,
          left: -15,
          color: "#aaa",
        }}
        onClick={() => handleOnRemoveImage(i)}
      >
        <CancelIcon />
      </IconButton>
      <img
        src={file.type !== "application/pdf" ? URL.createObjectURL(file) : "/pdf_icon.svg"}
        style={{
          width: "100%",
          maxWidth: "80px",
          maxHeight: "80px",
        }}
      />
      <Box
        sx={{
          width: "80px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <Typography variant="caption">{file.name}</Typography>
      </Box>
    </Box>
  ));

  return (
    <>
      <Stack spacing={2}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box sx={{ mb: 2 }}>
            <Typography fontSize="14px" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
              件名
            </Typography>
            <Controller
              name="shortAppealMessage"
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={Boolean(error)}
                  helperText={error?.message || ""}
                  placeholder="件名"
                  fullWidth
                  inputProps={{ maxLength: 70 }}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Box>
          <Controller
            name="proposal_content"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                error={Boolean(error)}
                helperText={error?.message || `${field.value?.length || 0}/1000`}
                placeholder="提案メッセージを入力してください"
                multiline
                minRows={3}
                maxRows={18}
                fullWidth
                inputProps={{ maxLength: 1000 }}
                variant="outlined"
                size="small"
                sx={{ mb: 1 }}
              />
            )}
          />
          <Box sx={{ mb: 2, width: 250, maxWidth: "100%" }}>
            <Typography fontSize="14px" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
              送信予約日時
            </Typography>
            <Controller
              name="scheduledAt"
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type="datetime-local"
                  error={Boolean(error)}
                  helperText={error?.message || "未入力の場合はすぐに送信されます。"}
                  fullWidth
                  inputProps={{ min: datetimeLocalMin() }}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Box>
          <Stack direction="row" justifyContent="space-between">
            {/* TODO: 2024/07/20 user UI/UX renewal対応の添付ファイル表示実装が間に合っていないため対応までコメントアウト
                <Stack direction="row" alignItems="center">
                  <label htmlFor="file-input-property">
                    <Button
                      // TODO: 送信可能ファイル最大数を確認する
                      // disabled={images.length >= maxImagesUpload}
                      label="ファイル選択"
                      component="span"
                      variant="outlined"
                      fronticon="attachFile"
                    />
                    <input
                      id="file-input-property"
                      type="file"
                      multiple
                      accept="image/png,image/jpg,image/jpeg,image/gif,application/pdf"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnAddImage(e)}
                      style={{ display: "none" }}
                    />
                  </label>
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    画像（JPG, PNG, GIF）またはPDF 10MB以下
                  </Typography>
                </Stack>*/}
            {props.proposal?.has_matched ? (
              <Button label="マッチ済み" type="button" style={{ background: "#E0E0E0" }} disabled />
            ) : hasProposed ? (
              <Button
                label="再送信する"
                type="submit"
                color="primary"
                variant="contained"
                disabled={
                  (props.isBulkReProposal && bulkTargetCount === 0) ||
                  (!props.isBulkReProposal && hasProposedToday) ||
                  overBulkCap
                }
                style={
                  (props.isBulkReProposal && bulkTargetCount === 0) ||
                  (!props.isBulkReProposal && hasProposedToday) ||
                  overBulkCap
                    ? undefined
                    : { background: "#5772FF" }
                }
              />
            ) : (
              <Button label="送信する" type="submit" disabled={overBulkCap} />
            )}
          </Stack>
          {/* TODO: 2024/07/20 user UI/UX renewal対応の添付ファイル表示未対応のため対応完了までコメントアウト
              {hasProposed &&
                <Stack direction="row" sx={{ m: 2 }} alignItems="center" gap={2}>
                  {sentFileUrls && sentFileUrls.map((url, index) => (
                    <Button
                      key={index}
                      fronticon="attachFile"
                      label={`送信済ファイル ${index + 1}`}
                      onClick={() => window.open(url, "_blank")}
                    />
                  ))}
                </Stack>
              }
              <Stack direction="row" sx={{ m: 2 }} alignItems="center"></Stack>
              <Stack direction="row" sx={{ m: 2 }} alignItems="center">
                {previewList}
              </Stack>*/}
        </form>
      </Stack>
      <Modal
        isDisplayed={blockedModalOpen}
        insideText={"本日すでにこのユーザーへ提案済みのため、\n再提案は行えません。"}
        onClickProcessButton={() => {
          setBlockedModalOpen(false);
          setBlockedAcknowledged(true);
        }}
        processBLabel=""
        cancelBLabel="OK"
        variant="oneButton"
      />
      {hasProposed && (
        <Modal
          isDisplayed={modalOpen}
          insideText={
            bulkTargetCount > 1
              ? `${bulkTargetCount}名のユーザーに\n一括で再提案しますか？`
              : "このユーザーへ既に投資提案しています。\n再提案しますか？"
          }
          onClickProcessButton={() => void handleClickProcessButton()}
          onClickCancelButton={() => setModalOpen(false)}
          processBLabel="提案する"
          cancelBLabel="キャンセル"
          variant="twoButton"
        />
      )}
      <Modal
        isDisplayed={replaceModalOpen}
        insideText={replaceModalText}
        onClickProcessButton={handleClickReplaceButton}
        onClickCancelButton={handleCancelReplace}
        processBLabel="置き換える"
        cancelBLabel="キャンセル"
        variant="twoButton"
      />
      <ConfirmDialog
        isDisplayed={sendingStatus === "hasSent"}
        insideText={completionText}
        ButtonLabel="閉じる"
        onClickButton={() => navigate(-1)}
      />
    </>
  );
};

export default InvestmentProposalForm;
